import { useEffect, useRef } from 'react';

/**
 * Keeps a public form's typed values across a language switch.
 *
 * Switching language is a real navigation to the other locale's URL, so React
 * unmounts the page and plain `useState` is gone. The draft is stored under a
 * locale-independent key, which is what makes /report-fake/ and
 * /de/faelschung-melden/ share it. It stays in sessionStorage: the point is one
 * uninterrupted visit, not a saved draft, and nothing typed into a fraud report
 * should outlive the tab.
 *
 * Fields listed in `exclude` are never written, so an uploaded photo or a
 * blob URL that only exists on the previous page cannot be restored broken.
 */
export function useFormDraft<T extends Record<string, unknown>>(
  key: string,
  form: T,
  restore: (draft: Partial<T>) => void,
  exclude: readonly (keyof T)[] = [],
): void {
  const restored = useRef(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const raw = sessionStorage.getItem(`checkcoa_draft_${key}`);
      if (raw) restore(JSON.parse(raw) as Partial<T>);
    } catch { /* A draft is a convenience; a bad one is simply skipped. */ }
  }, [key, restore]);

  useEffect(() => {
    if (!restored.current) return;
    const keep = Object.fromEntries(Object.entries(form)
      .filter(([field, value]) => !exclude.includes(field as keyof T) && typeof value !== 'object'));
    try {
      if (Object.values(keep).some(value => value !== '' && value !== false && value !== undefined)) {
        sessionStorage.setItem(`checkcoa_draft_${key}`, JSON.stringify(keep));
      } else {
        sessionStorage.removeItem(`checkcoa_draft_${key}`);
      }
    } catch { /* Storage is optional. */ }
  }, [key, form, exclude]);
}

export function clearFormDraft(key: string): void {
  try { sessionStorage.removeItem(`checkcoa_draft_${key}`); } catch { /* Storage is optional. */ }
}
